// The made-up client and the daily series every demo fixture is cut from.
// Deterministic (seeded) so the numbers are the same on every call and every
// reload; dates roll with today so the portal always looks current.

export const DEMO_CLIENT = {
  id: 990001,
  name: 'Summit Ridge Home Remodeling',
  slug: 'summit-ridge-home-remodeling',
  fee_pct: 11,
  parent_company: null,
  status: 'active',
  contact_name: 'Dana Whitfield',
  contact_email: 'dana@summitridge.example',
  phone: '(704) 555-0142',
  website: 'https://summitridge.example',
  timezone: 'America/New_York',
};

export const VERTICALS = [
  { id: 1, name: 'Bath', cpl: 92, apptRate: 0.42, issueRate: 0.75, closeRate: 0.24, ticket: 14000, share: 0.45 },
  { id: 2, name: 'Windows', cpl: 105, apptRate: 0.38, issueRate: 0.72, closeRate: 0.22, ticket: 13500, share: 0.33 },
  { id: 3, name: 'Roofing', cpl: 138, apptRate: 0.36, issueRate: 0.72, closeRate: 0.25, ticket: 17000, share: 0.22 },
];

export const MARKETS = [
  { id: 1, name: 'Charlotte', dma: 'Charlotte', state: 'NC', share: 0.5 },
  { id: 2, name: 'Raleigh-Durham', dma: 'Raleigh-Durham (Fayetteville)', state: 'NC', share: 0.32 },
  { id: 3, name: 'Greensboro', dma: 'Greensboro-High Point-Winston Salem', state: 'NC', share: 0.18 },
];

// Real ZIPs in each market so the ZIP Coverage map draws in the right place.
export const MARKET_ZIPS = {
  Charlotte: [
    '28202', '28203', '28204', '28205', '28207', '28209', '28210', '28211', '28226', '28227',
    '28262', '28269', '28270', '28277', '28078', '28031', '28036', '28105', '28104', '28173',
    '28079', '28025', '28027', '28115', '28117',
  ],
  'Raleigh-Durham': [
    '27601', '27603', '27604', '27605', '27606', '27607', '27608', '27609', '27612', '27613',
    '27615', '27617', '27511', '27513', '27518', '27519', '27560', '27587', '27703', '27705',
    '27707', '27713',
  ],
  Greensboro: [
    '27401', '27403', '27405', '27406', '27407', '27408', '27410', '27455', '27101', '27103',
    '27104', '27106', '27262', '27265',
  ],
};

// mulberry32 — tiny seeded PRNG.
export function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pick(r, list) {
  return list[Math.floor(r() * list.length)];
}

export function round2(n) {
  return Math.round(n * 100) / 100;
}

export function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function today() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function monthKey(d) {
  return iso(d).slice(0, 7);
}

const HISTORY_DAYS = 400;
const MONTHLY_BUDGET = 62000; // ad spend, before fee

let cachedDaily = null;

// One row per day × vertical × market, yesterday back HISTORY_DAYS.
// Spend grows ~25% over the year (the "we scaled you" story) and leads follow
// spend at a CPL that drifts slowly, with weekend dips.
export function dailyRows() {
  if (cachedDaily) return cachedDaily;
  const r = rng(42);
  const end = addDays(today(), -1);
  const rows = [];
  for (let i = HISTORY_DAYS - 1; i >= 0; i -= 1) {
    const d = addDays(end, -i);
    const growth = 0.8 + 0.2 * ((HISTORY_DAYS - i) / HISTORY_DAYS) ** 1.3;
    const dow = d.getDay();
    const weekday = dow === 0 || dow === 6 ? 0.82 : 1.05;
    const dayBudget = (MONTHLY_BUDGET / 30.4) * growth * weekday;
    VERTICALS.forEach((v) => {
      MARKETS.forEach((m) => {
        const spend = dayBudget * v.share * m.share * (0.85 + r() * 0.3);
        const cpl = v.cpl * (0.85 + r() * 0.35) * (1.06 - 0.08 * growth);
        const expected = spend / cpl;
        const leads = Math.max(0, Math.round(expected + (r() - 0.5) * Math.sqrt(expected) * 1.4));
        rows.push({
          date: iso(d),
          vertical: v.name,
          vertical_id: v.id,
          market: m.name,
          market_id: m.id,
          spend: round2(spend),
          impressions: Math.round(spend * (55 + r() * 20)),
          clicks: Math.round(spend * (0.9 + r() * 0.5)),
          leads,
        });
      });
    });
  }
  cachedDaily = rows;
  return rows;
}

export function groupBy(rows, keyFn) {
  const out = new Map();
  rows.forEach((row) => {
    const k = keyFn(row);
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(row);
  });
  return out;
}

export function monthlyBudget(monthDate = today()) {
  const r = rng(Number(monthKey(monthDate).replace('-', '')));
  return Math.round((MONTHLY_BUDGET * (0.97 + r() * 0.06)) / 500) * 500;
}

// `next` link for paginated fixtures: same path and filters, next page.
export function nextUrl(path, params, page, size, total) {
  if (page * size >= total) return null;
  const p = new URLSearchParams(params);
  p.set('page', String(page + 1));
  return `${path.replace(/\/+$/, "")}/?${p.toString()}`;
}
